import React, {useEffect, useState, useRef, useCallback} from "react";
import {useParams} from "react-router-dom";
import {BaseType} from "../../types/Enums";
import {translate} from "../../types/EnumTranslations";
import { NAVIGABLE_BASE_TYPES } from '../../Helpers/baseTypeUtils';
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent, BreadcrumbItem} from "../../Components/BreadCrumb";
import {BaseComponentImpl, RoomImpl, LevelImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {Building, PhotoModel} from "../../types/protocols";
import ComponentDetailsView from "./ComponentDetailsView";
import { useFirebaseImage } from "../../Helpers/ImageLoader";
import { Fancybox } from "@fancyapps/ui";

// Component for image preview with proper Firebase loading
const ImagePreview: React.FC<{
    photo: PhotoModel;
    zoomLevel: number;
    panPosition: { x: number; y: number };
    onWheel: (e: React.WheelEvent) => void;
    onClick: (imageUrl: string) => void;
    onMouseDown: (e: React.MouseEvent) => void;
    alt: string;
    isDragging: boolean;
}> = ({ photo, zoomLevel, panPosition, onWheel, onClick, onMouseDown, alt, isDragging }) => {
    const { src, isLoading, hasError } = useFirebaseImage(photo.storagePath);
    
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#666'
            }}>
                Laddar bild...
            </div>
        );
    }
    
    if (hasError) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#999',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
            }}>
                Kunde inte ladda bild
            </div>
        );
    }
    
    return (
        <img
            src={src}
            alt={alt}
            style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                transition: isDragging ? 'none' : 'transform 0.1s ease',
                objectFit: 'contain',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none'
            }}
            onWheel={onWheel}
            onClick={() => onClick(src)}
            onMouseDown={onMouseDown}
            draggable={false}
        />
    );
};

const ComponentPage: React.FC = () => {
    const [component, setComponent] = useState<BaseComponentImpl | null>(null);
    const {buildingKey, itemId } = useParams();
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    
    // State for selected image and zoom
    const [selectedImage, setSelectedImage] = useState<PhotoModel | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasDragged, setHasDragged] = useState(false);
    const previewRef = useRef<HTMLDivElement>(null);

    function resolveRoomFromComponentOrParents(
        component: BaseComponentImpl,
        currentBuilding: Building | null,
    ): RoomImpl | undefined {
        if (!currentBuilding?.model?.structure) return undefined;
        const { rooms = {}, components = {} } = currentBuilding.model.structure;

        const roomFromKey = (rk?: string) => {
            if (!rk) return undefined;
            const rm = rooms[rk];
            return rm ? new RoomImpl(rm) : undefined;
        };

        const directRoom = roomFromKey(component.roomKey);
        if (directRoom) return directRoom;

        const parentRoomKeys = component.parentRoomKeys?.value;
        if (Array.isArray(parentRoomKeys)) {
            for (const rk of parentRoomKeys) {
                const r = roomFromKey(rk);
                if (r) return r;
            }
        }

        const parentSurfaceKeys: Array<string | undefined> = [
            component.parentSurface?.value ?? undefined,
            component.parentSurface1?.value ?? undefined,
            component.parentSurface2?.value ?? undefined,
        ];


        for (const pKey of parentSurfaceKeys) {
            if (!pKey) continue;
            const parentCompRaw = components[pKey];
            if (!parentCompRaw) continue;
            const parentComp = new BaseComponentImpl(parentCompRaw);

            const parentDirectRoom = roomFromKey(parentComp.roomKey);
            if (parentDirectRoom) return parentDirectRoom;

            const parentPRK = parentComp.parentRoomKeys?.value;
            if (Array.isArray(parentPRK)) {
                for (const rk of parentPRK) {
                    const r = roomFromKey(rk);
                    if (r) return r;
                }
            }
        }

        return undefined;
    }

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    useEffect(()=> {
        if (currentBuilding && itemId) {
            const data = currentBuilding?.model.structure?.components?.[itemId] ?? null;
            if (data) {
                setComponent(new BaseComponentImpl(data))
            }
        }
    }, [currentBuilding, itemId])

    // Handle image selection
    const handleImageSelect = useCallback((photo: PhotoModel) => {
        setSelectedImage(photo);
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
        setHasDragged(false);
    }, []);

    // Handle mouse wheel zoom
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoomLevel(prev => Math.max(0.1, Math.min(5, prev * delta)));
    }, []);

    // Handle mouse down to start panning
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
        setHasDragged(false);
    }, [panPosition]);

    // Handle mouse move to pan
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging) return;
        
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        
        // If moved more than 5 pixels, consider it a drag
        const distance = Math.sqrt(Math.pow(newX - panPosition.x, 2) + Math.pow(newY - panPosition.y, 2));
        if (distance > 5) {
            setHasDragged(true);
        }
        
        setPanPosition({ x: newX, y: newY });
    }, [isDragging, dragStart, panPosition]);

    // Handle mouse up to stop panning
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle preview click to open in FancyBox (only if not dragging)
    const handlePreviewClick = useCallback((imageUrl: string) => {
        // Don't open FancyBox if the user was dragging
        if (hasDragged) {
            return;
        }
        
        if (selectedImage && imageUrl && currentBuilding) {
            Fancybox.show([{
                src: imageUrl,
                caption: component?.displayName(currentBuilding) || 'Bild'
            }]);
        }
    }, [selectedImage, hasDragged, component, currentBuilding]);

    if (loadingBuilding) return <p></p>;
    if (buildingError) return <p style={{color: "#ff0000"}}>{buildingError}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!component) return <p>No component data available</p>;

    // Check if component has photos
    const hasPhotos = component.photos && Object.keys(component.photos).length > 0;

    const crumbs: BreadcrumbItem[] = [];
    crumbs.push( {
        text: currentBuilding.summary.name ?? "Byggnad",
        link: `/building/${buildingKey}`
    })

    let room: RoomImpl | undefined;

    room = resolveRoomFromComponentOrParents(component, currentBuilding);

    if (room) {
        if (room.levelKey) {
            const levelData = currentBuilding.model.structure?.levels?.[room.levelKey];
            if (levelData) {
                const level = new LevelImpl(levelData);
                crumbs.push({
                    text: level.displayName(),
                    link: `/building/${buildingKey}/level/${level.key}`,
                });
            }
        }

        crumbs.push({
            text: `${room.displayName()}`,
            link: `/building/${buildingKey}/room/${room.key}`,
        });
    }

    const baseTypes = NAVIGABLE_BASE_TYPES;

    if (baseTypes.includes(component.type)) {
        crumbs.push({
            text: translate(component.type),
            link: `/building/${buildingKey}/${component.type}`
        });
    }
    
    // For sheet metal components, add parent component to breadcrumb
    if (component.type === BaseType.SheetMetal) {
        const parentSurfaceKey = (component as any).parentSurface?.value;
        if (parentSurfaceKey && currentBuilding?.model?.structure?.components) {
            const parentComponent = currentBuilding.model.structure.components[parentSurfaceKey];
            if (parentComponent) {
                const parentImpl = new BaseComponentImpl(parentComponent);
                
                // Add parent type link (e.g., "Tak" or "Fasad")
                if (baseTypes.includes(parentImpl.type)) {
                    crumbs.push({
                        text: translate(parentImpl.type),
                        link: `/building/${buildingKey}/${parentImpl.type}`
                    });
                }
                
                // Add parent component link (e.g., "Tak Norr")
                crumbs.push({
                    text: parentImpl.displayName(currentBuilding),
                    link: `/building/${buildingKey}/component/${parentImpl.key}`
                });
            }
        }
    }

    crumbs.push( {
        text: component.displayName(currentBuilding)
    })

    return (
        <div style={{
            backgroundColor: "#f4f4f4",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            <Helmet>
                <title>
                    {`${translate(component.type)}: ${component.displayName(currentBuilding)}`}
                </title>
            </Helmet>

            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={crumbs}
                />
            </div>

            <div style={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden'
            }}>
                {/* Left Panel - Component Details (650px width) */}
                <div style={{
                    width: '650px',
                    backgroundColor: '#f4f4f4',
                    borderRight: '1px solid #dee2e6',
                    overflow: 'auto',
                    padding: '20px'
                }}>
                    <h3>{component.displayName(currentBuilding)}</h3>
                    
                    <div
                        style={{
                            width: "100%",
                            borderRadius: "5px",
                            padding: "30px",
                            backgroundColor: "#fff",
                            textAlign: "left",
                            boxShadow: '0 8px 8px rgba(0, 0, 0, 0.03)',
                        }}
                    >
                        {currentBuilding && buildingKey && (
                            <ComponentDetailsView
                                component={component}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                showHeader={false}
                                containerStyle={{
                                    backgroundColor: 'transparent',
                                    borderRadius: '0',
                                    padding: '0',
                                    boxShadow: 'none'
                                }}
                                onImageClick={hasPhotos ? handleImageSelect : undefined}
                            />
                        )}
                    </div>
                </div>

                {/* Right Panel - Image Preview (flex: 1) */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#fff',
                    overflow: 'hidden'
                }}>
                    {hasPhotos ? (
                        <>
                            {selectedImage ? (
                                <div
                                    ref={previewRef}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f5f5f5',
                                        cursor: isDragging ? 'grabbing' : 'grab'
                                    }}
                                    onWheel={handleWheel}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                >
                                    <ImagePreview
                                        photo={selectedImage}
                                        zoomLevel={zoomLevel}
                                        panPosition={panPosition}
                                        onWheel={handleWheel}
                                        onClick={handlePreviewClick}
                                        onMouseDown={handleMouseDown}
                                        alt={component.displayName(currentBuilding)}
                                        isDragging={isDragging}
                                    />
                                </div>
                            ) : (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#666',
                                    fontSize: '18px'
                                }}>
                                    Välj en bild från listan för att visa förhandsvisning
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '18px'
                        }}>
                            Inga bilder tillgängliga
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComponentPage;
